import { CommonStatusTag } from 'components/CommonStatusTag';

import { colors } from 'constants/colors';
import { TaskState, TourState } from 'data/__generated__';

import { TASK_STATE_LABELS, tourStateLabel } from '../helpers';
import { TASK_COLOR, TASK_DONE_COLOR } from './StopStatus';

/**
 * Same shape as the deliveries' `getColorsFromState`: a switch, read when the tag renders — a module-level
 * `Record<TourState, …>` reads the enum while the generated module is still initialising.
 */
const getColorsFromTourState = (state: TourState): { backgroundColor: string; labelColor: string } => {
    const { black, blue, green, lightGrey, pantinGrey, pureWhite } = colors;

    switch (state) {
        case TourState.Ready:
            return { backgroundColor: black, labelColor: pureWhite };
        case TourState.InDelivery:
            return { backgroundColor: blue, labelColor: pureWhite };
        case TourState.Finished:
            return { backgroundColor: green, labelColor: black };
        case TourState.Archived:
            return { backgroundColor: pantinGrey, labelColor: pureWhite };
        case TourState.Created:
        default:
            return { backgroundColor: lightGrey, labelColor: black };
    }
};

/**
 * The tour's own DRIVING state — the pendant of the deliveries' `StatusTag`, so both read the same way, and
 * also what the state history renders record by record. The box return is not a state, and not a status
 * either: it has its own block on the tour's page (`BoxesRecap`), read off the boxes.
 */
export const TourStatusTag = ({ state, showHeading }: { state: TourState; showHeading?: boolean }) => {
    const { backgroundColor, labelColor } = getColorsFromTourState(state);

    return (
        <CommonStatusTag
            label={tourStateLabel(state)}
            backgroundColor={backgroundColor}
            labelColor={labelColor}
            showHeading={showHeading}
        />
    );
};

/** The deliveries' colours, but violet in progress and another green once done: a task never passes for a delivery. */
const getColorsFromTaskState = (state: TaskState): { backgroundColor: string; labelColor: string } => {
    const { black, lightGrey, pantinGrey, pureWhite, red } = colors;

    switch (state) {
        case TaskState.InProgress:
            return { backgroundColor: TASK_COLOR, labelColor: pureWhite };
        case TaskState.Finished:
            return { backgroundColor: TASK_DONE_COLOR, labelColor: pureWhite };
        case TaskState.Failed:
            return { backgroundColor: pantinGrey, labelColor: pureWhite };
        case TaskState.Aborted:
            return { backgroundColor: red, labelColor: pureWhite };
        case TaskState.Created:
        default:
            return { backgroundColor: lightGrey, labelColor: black };
    }
};

export const TaskStatusTag = ({ state }: { state: TaskState }) => {
    const { backgroundColor, labelColor } = getColorsFromTaskState(state);

    return (
        <CommonStatusTag label={TASK_STATE_LABELS[state]} backgroundColor={backgroundColor} labelColor={labelColor} />
    );
};

/** What the manager must go and look at — a tag, not a colour bar, so it reads like every other status here. */
export const IssueTag = ({ label }: { label: string }) => (
    <CommonStatusTag label={label} backgroundColor={colors.red} labelColor={colors.pureWhite} />
);
